import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import DropdownMenu from "components/common/DropdownMenu";
import AppState from "store/AppState";
import {
  selectCurrentStoreId,
  selectStoreInfoById,
} from "store/store-list/StoreListSelectors";
import {
  clearLinkedCategorySearch,
  fetchAllLinkedCategories,
  fetchLinkedCategoryCodes,
  searchLinkedCategories,
} from "store/linked-categories/LinkedCategoriesActions";
import {
  selectCategorySearchLoading,
  selectCategorySearchResults,
  selectLinkedCategories,
  selectLinkedCategoriesLoading,
  selectLinkedCategoryLabels,
} from "store/linked-categories/LinkedCategoriesSelectors";
import {
  CategoryLabel,
  CategorySearchResult,
  LinkedCategoryEntry,
  LinkedCategoryPane,
} from "store/linked-categories/LinkedCategoriesTypes";
import {
  ActionColumn,
  Container,
  PaneWrapper,
  CategorySetBox,
  CategorySetRow,
  StatusMessage,
  fieldStyles,
} from "./LinkedCategoriesSearchStyles";

const DEBOUNCE_DELAY = 500;
const MAX_OPTIONS = 5;
const DELIMITER = " : ";

export const categoryKey = (
  category: CategorySearchResult | LinkedCategoryEntry,
): string =>
  `${category.storeId}:${category.catalogId}:${category.categoryId}`;

// Legacy labels a category "identifier : owning catalog : owning store", and
// takes all three from the server rather than resolving any of them here.
// A link whose category no longer exists has no label, so it shows the ids
// that were stored for it, which is all legacy ever displayed.
export const formatCategory = (
  category: CategorySearchResult | LinkedCategoryEntry,
  labels: Record<string, CategoryLabel>,
): string => {
  const label = labels[categoryKey(category)];
  return label
    ? [label.code, label.catalogCode, label.storeCode].join(DELIMITER)
    : [category.categoryId, category.catalogId, category.storeId].join(
        DELIMITER,
      );
};

const isSameCategory = (
  a: LinkedCategoryEntry,
  b: CategorySearchResult | null,
): boolean =>
  !!b &&
  a.categoryId === b.categoryId &&
  a.catalogId === b.catalogId &&
  a.storeId === b.storeId;

const SearchPane = ({ pane }: { pane: LinkedCategoryPane }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentStoreId = useSelector(selectCurrentStoreId);
  const [storeId, setStoreId] = useState<string>(currentStoreId ?? "");
  const storeInfo = useSelector((state: AppState) =>
    selectStoreInfoById(state, storeId),
  );
  
  const catalogId = storeInfo?.storeDefaults?.catalogId ?? "";
  const results = useSelector((state: AppState) =>
    selectCategorySearchResults(state, pane),
  );
  const loading = useSelector((state: AppState) =>
    selectCategorySearchLoading(state, pane),
  );
  const allLinkedSets = useSelector(selectLinkedCategories);
  const setsLoading = useSelector(selectLinkedCategoriesLoading);
  const resolvedLabels = useSelector(selectLinkedCategoryLabels);

  // Search results are already labelled; stored set members are resolved.
  const labels = useMemo(
    () => ({
      ...resolvedLabels,
      ...Object.fromEntries(
        results.map((r) => [
          categoryKey(r),
          { code: r.code, catalogCode: r.catalogCode, storeCode: r.storeCode },
        ]),
      ),
    }),
    [resolvedLabels, results],
  );
  const label = useCallback(
    (category: CategorySearchResult | LinkedCategoryEntry) =>
      formatCategory(category, labels),
    [labels],
  );
  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState<CategorySearchResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const runSearch = useCallback(
    (startsWith: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = startsWith.trim();
      if (!trimmed || !storeId || !catalogId) {
        dispatch(clearLinkedCategorySearch(pane));
        return;
      }
      debounceRef.current = setTimeout(() => {
        dispatch(
          searchLinkedCategories({
            pane,
            storeId,
            catalogId,
            startsWith: trimmed,
            limit: MAX_OPTIONS,
          }),
        );
      }, DEBOUNCE_DELAY);
    },
    [catalogId, dispatch, pane, storeId],
  );

  // Legacy lists the whole group with the selected category included, and falls
  // back to the selected category alone when it belongs to no group.
  const categorySet: LinkedCategoryEntry[] = useMemo(() => {
    if (!selected) return [];
    const group = allLinkedSets.find((set) =>
      set.categories.some((category) => isSameCategory(category, selected)),
    );
    return group && group.categories.length > 0
      ? group.categories
      : [selected];
  }, [allLinkedSets, selected]);

  // Resolve identifiers for set members the search has not already labelled.
  useEffect(() => {
    const unresolved = categorySet.filter(
      (category) => !labels[categoryKey(category)],
    );
    if (unresolved.length) dispatch(fetchLinkedCategoryCodes(unresolved));
  }, [categorySet, labels, dispatch]);

  const statusMessage = !selected
    ? ""
    : setsLoading
      ? intl.formatMessage({
          id: "linkedCategoriesSearch.loadingSet",
          defaultMessage: "Loading linked categories...",
        })
      : categorySet.length <= 1
        ? intl.formatMessage({
            id: "linkedCategoriesSearch.noLinkedCategories",
            defaultMessage: "No linked categories found.",
          })
        : intl.formatMessage(
            {
              id: "linkedCategoriesSearch.linkedCategoryCount",
              defaultMessage:
                "{count, plural, one {# category} other {# categories}} linked to the category selected.",
            },
            { count: categorySet.length - 1 },
          );

  const handleStoreChange = (nextStoreId: string) => {
    setStoreId(nextStoreId);
    setInputValue("");
    setSelected(null);
    dispatch(clearLinkedCategorySearch(pane));
  };

  return (
    <PaneWrapper>
      <Typography variant="title">
        {intl.formatMessage({
          id: "linkedCategoriesSearch.selectStore",
          defaultMessage: "Select a store:",
        })}
      </Typography>
      <DropdownMenu
        menuId="LinkedCategoriesStore"
        name={pane}
        value={storeId}
        changeHandler={(event) => handleStoreChange(event.target.value)}
      />

      <Typography variant="title">
        {intl.formatMessage({
          id: "linkedCategoriesSearch.searchLabel",
          defaultMessage:
            "Type in Category Identifier to search (case sensitive):",
        })}
      </Typography>
      <Autocomplete
        id={`lcs-search-${pane}`}
        options={results}
        filterOptions={(options) => options}
        getOptionLabel={(option) => label(option)}
        isOptionEqualToValue={(option, value) =>
          option.categoryId === value.categoryId &&
          option.catalogId === value.catalogId &&
          option.storeId === value.storeId
        }
        value={selected}
        onChange={(event, value) => setSelected(value)}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => {
          setInputValue(value);
          if (reason === "input") runSearch(value);
        }}
        loading={loading}
        noOptionsText={intl.formatMessage({
          id: "linkedCategoriesSearch.typeToSearch",
          defaultMessage: "Type to search",
        })}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            sx={fieldStyles}
            placeholder={intl.formatMessage({
              id: "linkedCategoriesSearch.placeholder",
              defaultMessage:
                "Category Identifier : Owning Catalog Identifier : Owning Store Identifier",
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <StatusMessage variant="subtitle2">{statusMessage}</StatusMessage>
      <CategorySetBox data-testid={`lcs-category-set-${pane}`}>
        {categorySet.map((category) => (
          <CategorySetRow
            variant="linkedCategoriesText"
            key={categoryKey(category)}
            title={label(category)}
            selected={isSameCategory(category, selected)}
          >
            {label(category)}
          </CategorySetRow>
        ))}
      </CategorySetBox>
    </PaneWrapper>
  );
};

export const LinkedCategoriesSearch = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAllLinkedCategories());
  }, [dispatch]);

  return (
    <Container>
      <SearchPane pane="target" />
      <ActionColumn />
      <SearchPane pane="source" />
    </Container>
  );
};

export default LinkedCategoriesSearch;
